import {Space, Table, Tooltip} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {
  ChargeStatus,
  Maybe,
  PaymentMethod,
  SearchableSubscriptionFilterInput,
  SubscriptionFieldsFragment,
  SubscriptionStatus,
  useSubscriptionsQuery
} from 'api/generated';
import {ButtonLink, Hint, PaymentMethodOverview, TableCard} from 'components';
import {SubscriptionStatusTag} from 'components/SubscriptionStatusTag';
import deepEqual from 'deep-equal';
import {getChargeCustomer} from 'lib/chargeUtils';
import {omitEmpty} from 'lib/omitEmpty';
import {getIntervalLabel} from 'lib/subscriptionUtils';
import {dateFormat} from 'lib/utils';
import moment from 'moment';
import React, {useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {QueryParamConfig} from 'serialize-query-params/lib/types';
import styled from 'styled-components';
import {
  BooleanParam,
  DelimitedArrayParam,
  NumberParam,
  StringParam,
  useQueryParam,
  useQueryParams
} from 'use-query-params';
import {FilterQueryParams, SubscriptionsFilters} from './SubscriptionsFilters';

export const queryFiltersSchema = {
  customerEmail: StringParam,
  customerName: StringParam,
  customerPhone: StringParam,
  id: StringParam,
  sequenceId: StringParam,
  storeId: BooleanParam,
  interval: StringParam,
  paymentMethodCardLast4: StringParam,
  paymentMethodCardBrand: StringParam,
  lastPaymentId: StringParam,
  lastOrderId: StringParam,
  createdAt: DelimitedArrayParam
};
const textFields: string[] = [];
const booleanFields: string[] = [];

const Amount = styled.span`
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
`;
const PaymentMethodIcon = styled.div`
  opacity: 0.8;
  overflow: visible;
`;

const SubscriptionsTable = styled(Table)`
  td {
    vertical-align: top;
  }
` as unknown as typeof Table;
const buildFilter = (
  queryFilters: {[key: string]: any},
  status: string,
  accountId?: string
): SearchableSubscriptionFilterInput => {
  const filter: SearchableSubscriptionFilterInput = {};
  if (status && status !== 'all') {
    filter.status = {match: status.toUpperCase()};
  }
  if (accountId) {
    filter.accountId = {match: accountId};
  }
  Object.entries(queryFilters).forEach(([key, value]) => {
    if (key === 'createdAt') {
      const range = value || [];
      filter.createdAt = {
        range: [moment(range[0]).startOf('day').unix(), moment(range[1]).endOf('day').unix()]
      };
      return;
    }
    if (!value) return;
    const queryType = textFields.includes(key)
      ? 'matchPhrase'
      : booleanFields.includes(key)
      ? 'exists'
      : 'match';
    (filter as any)[key] = {[queryType]: value};
  });

  return filter;
};

export const Subscriptions: React.FC<{accountId: string}> = ({accountId}) => {
  const history = useHistory();

  const [size = 20, setSize] = useQueryParam<number | undefined>(
    'size',
    NumberParam as QueryParamConfig<number | undefined>
  );
  const [from = 0, setFrom] = useQueryParam<number | undefined>(
    'from',
    NumberParam as QueryParamConfig<number | undefined>
  );
  const [status = 'all', setStatus] = useQueryParam<string | undefined>(
    'status',
    StringParam as QueryParamConfig<string | undefined>
  );
  const [queryFilters, setQueryFilters] = useQueryParams<{[key: string]: any}>(queryFiltersSchema);

  const filter = buildFilter(queryFilters, status, accountId);
  const {data, loading, refetch} = useSubscriptionsQuery({
    variables: {from, size, filter}
  });

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const subscriptions = data?.subscriptions?.items || [];
  const total = data?.subscriptions.total || undefined;

  const handleStatusChange = (status: string) => {
    setFrom(undefined, 'pushIn');
    if (status === 'all') {
      return setStatus(undefined, 'pushIn');
    }
    setStatus(status, 'pushIn');
  };

  const columns: Array<ColumnProps<SubscriptionFieldsFragment>> = [
    {
      title: 'Amount',
      dataIndex: 'amount',
      align: 'right',
      className: 'minimized-cell',
      render(amount: number, subscription) {
        const currency = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: subscription.currency as string
        });
        return <Amount>{currency.format(amount / 100)}</Amount>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      className: 'minimized-cell',
      render(status: ChargeStatus, subscription) {
        if (subscription.cancelAtPeriodEnd) {
          return (
            <Hint
              message={'This subscription will be canceled at the end of current period'}
              title={<SubscriptionStatusTag subscription={subscription} />}
            />
          );
        }
        if (subscription.pauseAtPeriodEnd) {
          return (
            <Hint
              message={'This subscription will be paused at the end of current period'}
              title={<SubscriptionStatusTag subscription={subscription} />}
            />
          );
        }
        return <SubscriptionStatusTag subscription={subscription} />;
      }
    },
    {
      title: 'Interval',
      dataIndex: 'interval',
      className: 'minimized-cell',
      render(_, subscription) {
        return getIntervalLabel(subscription);
      }
    },
    {
      title: 'Last order ID',
      dataIndex: 'lastOrderId',
      className: 'minimized-cell'
    },
    {
      title: 'Customer',
      dataIndex: ['customer', 'email'],
      render(email: string, subscription) {
        return getChargeCustomer(subscription);
      }
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      className: 'minimized-cell',
      render(_: Maybe<PaymentMethod>, subscription: SubscriptionFieldsFragment) {
        return (
          <PaymentMethodIcon>
            <PaymentMethodOverview item={subscription} minimal />
          </PaymentMethodIcon>
        );
      }
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      className: 'minimized-cell',
      render(date: number) {
        const anHourAgo = moment().subtract(59, 'minutes');
        const fullDate = moment.unix(date).format('lll');
        const relativeDate = moment.max(moment.unix(date), anHourAgo).isSame(anHourAgo)
          ? moment.unix(date).format(dateFormat.dateTime)
          : moment.unix(date).fromNow();
        return (
          <Tooltip placement="topLeft" title={fullDate}>
            {relativeDate}
          </Tooltip>
        );
      }
    }
  ];

  const tabList = [
    {
      key: 'active',
      tab: 'Active'
    },

    {
      key: 'trialing',
      tab: 'Trialing'
    },
    {
      key: 'paused',
      tab: 'Paused'
    },
    {
      key: 'canceled',
      tab: 'Canceled'
    },
    {
      key: 'past_due',
      tab: 'Past Due'
    },
    {
      key: 'all',
      tab: 'All'
    }
  ];

  const handleFiltersChange = (values: FilterQueryParams) => {
    if (deepEqual(omitEmpty(values), omitEmpty(queryFilters))) {
      return refetch();
    }
    setFrom(undefined, 'pushIn');
    setQueryFilters(values, 'pushIn');
  };

  return (
    <>
      <SubscriptionsFilters
        onChange={handleFiltersChange}
        queryFilters={queryFilters}
        accountId={accountId}
      />
      <TableCard
        size="small"
        tabBarExtraContent={
          <Space>
            <ButtonLink type="primary" to={`/accounts/${accountId}/subscriptions/new`}>
              Create Subscription
            </ButtonLink>
          </Space>
        }
        tabList={tabList}
        activeTabKey={status}
        onTabChange={(key: string) => handleStatusChange(key as SubscriptionStatus | 'all')}>
        <SubscriptionsTable
          scroll={{x: true}}
          dataSource={subscriptions}
          columns={columns}
          loading={loading}
          rowClassName={() => 'clickable'}
          rowKey="id"
          onRow={(subscription) => ({
            onClick: (e) => {
              e.preventDefault();
              const url = `/accounts/${accountId}/subscriptions/${subscription.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, subscription.id)?.focus();
              } else {
                history.push(url);
              }
            }
          })}
          pagination={{
            pageSize: size,
            current: from / size + 1,
            total,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total.toLocaleString()} subscriptions`,
            onChange: (page, size = 0) => setFrom((page - 1) * size, 'pushIn'),
            onShowSizeChange: (page, size) => setSize(size, 'pushIn')
          }}
        />
      </TableCard>
    </>
  );
};
